import React, {useEffect, useMemo, useState} from 'react';
import {Popup} from 'antd-mobile';
import {ProgressiveCircularLoading} from '@fe-progressive/package-progressive/progressive-loading';
import {Linking} from 'react-native';
import {useTranslation} from 'react-i18next';
import {getPayMethod} from '@/common-pages/recharge/recharge.service';
import {useSequentialModalContext} from '@/components/basic/sequential-modal/context';
import {useScreenSize} from '@/common-pages/hooks/size.hooks';
import {ReactComponent as GiftCloseIcon} from '@/assets/icons/recharge-gift/close.svg';
import {ReactComponent as SelectedCheckIcon} from '@/assets/icons/recharge-gift/selected-check.svg';
import {goTo} from '@/utils';
import {
  createRechargeGiftOrder,
  type RechargeGiftPayMethod,
  type RechargeGiftScene,
  type RechargeGiftTier,
} from './recharge-gift.service';
import styles from './RechargeGiftModal.module.scss';

const welcomeBonus = require('@/assets/imgs/recharge-gift/welcome-bonus.webp');
const prizeBg = require('@/assets/imgs/recharge-gift/prize-bg.webp');
const greenArrow = require('@/assets/imgs/recharge-gift/green-arrow.webp');
const withdrawBadge = require('@/assets/imgs/recharge-gift/24h-withdraw.webp');

const bigAmountAssets: Record<string, string> = {
  '₹': require('@/assets/imgs/recharge-gift/big-inr.webp'),
  ',': require('@/assets/imgs/recharge-gift/big-comma.webp'),
  '0': require('@/assets/imgs/recharge-gift/big-0.webp'),
  '1': require('@/assets/imgs/recharge-gift/big-1.webp'),
  '2': require('@/assets/imgs/recharge-gift/big-2.webp'),
  '3': require('@/assets/imgs/recharge-gift/big-3.webp'),
  '4': require('@/assets/imgs/recharge-gift/big-4.webp'),
  '5': require('@/assets/imgs/recharge-gift/big-5.webp'),
  '6': require('@/assets/imgs/recharge-gift/big-6.webp'),
  '7': require('@/assets/imgs/recharge-gift/big-7.webp'),
  '8': require('@/assets/imgs/recharge-gift/big-8.webp'),
  '9': require('@/assets/imgs/recharge-gift/big-9.webp'),
};

const smallAmountAssets: Record<string, string> = {
  '₹': require('@/assets/imgs/recharge-gift/sm-inr.webp'),
  ',': require('@/assets/imgs/recharge-gift/sm-comma.webp'),
  '+': require('@/assets/imgs/recharge-gift/sm-plus.webp'),
  '0': require('@/assets/imgs/recharge-gift/sm-0.webp'),
  '1': require('@/assets/imgs/recharge-gift/sm-1.webp'),
  '2': require('@/assets/imgs/recharge-gift/sm-2.webp'),
  '3': require('@/assets/imgs/recharge-gift/sm-3.webp'),
  '4': require('@/assets/imgs/recharge-gift/sm-4.webp'),
  '5': require('@/assets/imgs/recharge-gift/sm-5.webp'),
  '6': require('@/assets/imgs/recharge-gift/sm-6.webp'),
  '7': require('@/assets/imgs/recharge-gift/sm-7.webp'),
  '8': require('@/assets/imgs/recharge-gift/sm-8.webp'),
  '9': require('@/assets/imgs/recharge-gift/sm-9.webp'),
};

interface RechargeGiftModalProps {
  gift: RechargeGiftTier;
  scene: RechargeGiftScene;
  onShown?: () => void;
  onDismiss?: () => void;
  sequentialOverlayClassName?: string;
  sequentialConsume?: boolean;
  sequentialTransitionDuration?: number;
  sequentialTransitionType?: 'zoom-in-out';
}

interface ImageAmountProps {
  value: string;
  size: 'big' | 'small';
}

const formatRupeeAmount = (amount?: number | null) =>
  `₹${Math.max(0, Math.round(Number(amount) || 0)).toLocaleString('en-IN')}`;

const ImageAmount = ({value, size}: ImageAmountProps) => {
  const assets = size === 'big' ? bigAmountAssets : smallAmountAssets;
  return (
    <span className={styles[`${size}Amount`]} aria-label={value}>
      {Array.from(value).map((character, index) => {
        const asset = assets[character];
        return asset ? (
          <img
            key={`${character}-${index}`}
            src={asset}
            alt=""
            aria-hidden="true"
          />
        ) : null;
      })}
    </span>
  );
};

const openPaymentResult = async (
  result: Awaited<ReturnType<typeof createRechargeGiftOrder>>,
) => {
  if (typeof result === 'string') {
    window.location.href = result;
    return;
  }
  if (result.payStr) {
    await Linking.openURL(result.payStr);
  }
};

export function RechargeGiftModal({
  gift,
  scene,
  onShown,
  onDismiss,
}: RechargeGiftModalProps) {
  const {t} = useTranslation();
  const {screenWidth} = useScreenSize();
  const {goToNext, onClose} = useSequentialModalContext();
  const [channelVisible, setChannelVisible] = useState(false);
  const [channels, setChannels] = useState<RechargeGiftPayMethod[]>([]);
  const [selectedId, setSelectedId] = useState<number>();
  const [loading, setLoading] = useState(false);

  const selectedChannel = useMemo(
    () => channels.find(item => item.id === selectedId),
    [channels, selectedId],
  );
  const stageScale = Math.min(screenWidth / 375, 1);
  const rechargeAmount = formatRupeeAmount(gift.rechargeAmount);
  const giftAmount = formatRupeeAmount(gift.giftAmount);
  const totalAmount = formatRupeeAmount(gift.totalArrivalAmount);
  const giftPercent = gift.rechargeAmount
    ? Math.round((Number(gift.giftAmount) / gift.rechargeAmount) * 100)
    : 0;

  useEffect(() => {
    onShown?.();
  }, [onShown]);

  useEffect(() => {
    if (!channelVisible || channels.length > 0) {
      return;
    }
    setLoading(true);
    getPayMethod()
      .then(list => {
        const available = list.filter(item => {
          const amount = Number(gift.rechargeAmount || 0);
          return (
            (!item.minAmount || amount >= item.minAmount) &&
            (!item.maxAmount || amount <= item.maxAmount) &&
            item.payTag !== 'IM_RECHARGE'
          );
        });
        setChannels(available);
        setSelectedId(available[0]?.id);
      })
      .finally(() => setLoading(false));
  }, [channelVisible, channels.length, gift.rechargeAmount]);

  const handleConfirm = async () => {
    if (!selectedChannel || gift.currentTierNo == null) {
      return;
    }
    setLoading(true);
    try {
      const result = await createRechargeGiftOrder({
        payTypeId: selectedChannel.id,
        payTag: selectedChannel.payTag,
        tierNo: gift.currentTierNo,
        entryScene: scene,
        payCategory: 1,
        useType: 1,
      });
      setChannelVisible(false);
      onClose();
      await new Promise(resolve => setTimeout(resolve, 300));
      await openPaymentResult(result);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    onDismiss?.();
    goToNext();
  };

  const handleWithdraw = () => {
    onClose();
    window.setTimeout(() => goTo('Withdraw'), 180);
  };

  return (
    <>
      <div
        className={styles.stageViewport}
        style={{
          width: `${(375 * stageScale) / 16}rem`,
          height: `${(390 * stageScale) / 16}rem`,
        }}>
        <div
          className={styles.stage}
          style={{transform: `scale(${stageScale})`}}>
          <img className={styles.prizeBg} src={prizeBg} alt="" />
          <img
            className={styles.welcomeBonus}
            src={welcomeBonus}
            alt={t('rechargeGift.title')}
          />
          <div className={styles.totalAmount}>
            <ImageAmount value={totalAmount} size="big" />
          </div>
          <div className={styles.giftPercent}>
            <img src={greenArrow} alt="" />
            <span>+{giftPercent}%</span>
          </div>
          <div className={styles.amountBreakdown}>
            <ImageAmount
              value={`${rechargeAmount}+${giftAmount}`}
              size="small"
            />
          </div>
          <button
            type="button"
            className={styles.rechargeButton}
            onClick={() => setChannelVisible(true)}>
            {rechargeAmount}
          </button>
          <button
            type="button"
            className={styles.withdrawBadge}
            aria-label={t('rechargeGift.withdrawBadge')}
            onClick={handleWithdraw}>
            <img src={withdrawBadge} alt="" />
          </button>
          <button
            type="button"
            aria-label={t('rechargeGift.close')}
            className={styles.giftClose}
            onClick={handleDismiss}>
            <GiftCloseIcon />
          </button>
        </div>
      </div>

      <Popup
        visible={channelVisible}
        onMaskClick={() => !loading && setChannelVisible(false)}
        bodyClassName={styles.channelPopup}>
        <div className={styles.channelHeader}>
          <h2>{t('rechargeGift.selectChannel')}</h2>
          <button
            type="button"
            aria-label={t('rechargeGift.close')}
            onClick={() => !loading && setChannelVisible(false)}>
            ×
          </button>
        </div>

        <div className={styles.channelGrid}>
          {loading && channels.length === 0 ? (
            <div className={styles.channelLoading}>
              <ProgressiveCircularLoading size={32} />
            </div>
          ) : channels.length > 0 ? (
            channels.map(channel => {
              const selected = channel.id === selectedId;
              return (
                <button
                  type="button"
                  key={channel.id}
                  className={`${styles.channelCard} ${
                    selected ? styles.channelCardSelected : ''
                  }`}
                  onClick={() => setSelectedId(channel.id)}>
                  {selected ? (
                    <span className={styles.selectedCorner}>
                      <SelectedCheckIcon />
                    </span>
                  ) : null}
                  <span className={styles.channelIcon}>
                    {channel.payIcon ? (
                      <img src={channel.payIcon} alt="" />
                    ) : null}
                  </span>
                  <span className={styles.channelNameRow}>
                    <span>{channel.payName}</span>
                    {channel.activeDescription ? (
                      <strong>{channel.activeDescription}</strong>
                    ) : null}
                  </span>
                </button>
              );
            })
          ) : (
            <div className={styles.noChannel}>
              {t('rechargeGift.noChannel')}
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={!selectedChannel || loading}
          className={styles.confirmButton}
          onClick={handleConfirm}>
          {loading && channels.length > 0 ? (
            <ProgressiveCircularLoading
              size={20}
              color="#fff"
              trackColor="rgba(255, 255, 255, 0.35)"
              aria-label={String(t('loading'))}
            />
          ) : (
            t('rechargeGift.confirm')
          )}
        </button>
        <div className={styles.safeAreaIndicator} />
      </Popup>
    </>
  );
}

export default RechargeGiftModal;
